import { inject, Injectable, Type } from '@angular/core';
import { FormArray, FormControl, FormGroup, Validators } from '@angular/forms';
import { ICustomCtaValue, IGlobalCtaValue } from '@campaign-form/campaign-form.interface';
import { EDeliverableType } from '@enums/campaign.enum';
import { TranslateService } from '@ngx-translate/core';
import { ICustomFile } from '@shared/components/file-uploader/file-uploader.interface';
import { FileUploaderService } from '@shared/components/file-uploader/file-uploader.service';
import { imageExtensionValidator } from '@shared/validators/image-extension.validator';
import { v4 as uuidv4 } from 'uuid';
import { ContentComponent } from '../../content/content.component';
import { CustomComponent } from '../../custom/custom.component';
import {
  IDeliverableConfig,
  IDeliverableImage,
  IDeliverableImageForm,
  TDeliverable,
} from '../../deliverable-list.interface';
import { ShowcaseComponent } from '../../showcase/showcase.component';
import { IImageDeliverable, IImageDeliverableForm, TImageDeliverableType } from './image-deliverable.interface';

interface IImageServiceConfig {
  component: Type<ContentComponent | ShowcaseComponent | CustomComponent>;
  deliverableType: TImageDeliverableType;
  defaultNameTranslationKey: string;
}

@Injectable({
  providedIn: 'root',
})
export class ImageDeliverableService {
  private readonly translate = inject(TranslateService);

  public getConfig(config: IImageServiceConfig): IDeliverableConfig {
    return {
      component: config.component,
      createForm: (
        item?: TDeliverable,
        creatorId?: string,
        isViewMode?: boolean,
      ): FormGroup<IImageDeliverableForm> | null =>
        this.createImageDeliverableForm(config, item, creatorId, isViewMode),
    };
  }

  private createImageDeliverableForm(
    config: IImageServiceConfig,
    item?: TDeliverable,
    creatorId?: string,
    isViewMode?: boolean,
  ): FormGroup<IImageDeliverableForm> | null {
    if (item && item.type !== config.deliverableType) {
      return null;
    }

    const imageItem = this.toImageItem(item);
    const images = imageItem ? imageItem.images : [];

    const form = new FormGroup({
      customCtaValues: new FormControl<ICustomCtaValue[]>(imageItem ? (imageItem.customCtaValues ?? []) : []),
      description: new FormControl<string | null>(
        imageItem ? imageItem.description : null,
        isViewMode ? [] : [Validators.required, Validators.maxLength(4096)],
      ),
      exclusiveTo: new FormControl<string | null>((imageItem && imageItem.exclusiveTo) || creatorId || null),
      globalCtaValues: new FormControl<IGlobalCtaValue[] | null>(
        imageItem ? (imageItem.globalCtaValues ?? null) : null,
      ),
      id: new FormControl<string | null>(imageItem ? (imageItem.id ?? uuidv4()) : uuidv4(), Validators.required),
      images: this.buildImagesFormArray(images, creatorId, isViewMode),
      isNew: new FormControl<boolean>(!(imageItem && imageItem.id)),
      name: new FormControl<string | null>(
        imageItem ? imageItem.name : this.translate.instant(config.defaultNameTranslationKey),
        [Validators.required, Validators.maxLength(255)],
      ),
      sponsoredContentRelevant: new FormControl<boolean>(imageItem?.sponsoredContentRelevant ?? true, {
        nonNullable: true,
      }),
      type: new FormControl<EDeliverableType>(this.resolveDeliverableType(config), { nonNullable: true }),
    }) as FormGroup<IImageDeliverableForm>;

    if (isViewMode) {
      form.disable();
    }

    return form;
  }

  private resolveDeliverableType(config: IImageServiceConfig): EDeliverableType {
    if (config.deliverableType === EDeliverableType.content) {
      return EDeliverableType.content;
    }

    if (config.deliverableType === EDeliverableType.custom) {
      return EDeliverableType.custom;
    }

    return EDeliverableType.showcase;
  }

  private toImageItem(item?: TDeliverable): IImageDeliverable | undefined {
    if (!item) {
      return undefined;
    }

    if (
      item.type === EDeliverableType.content ||
      item.type === EDeliverableType.showcase ||
      item.type === EDeliverableType.custom
    ) {
      return item as IImageDeliverable;
    }

    return undefined;
  }

  private buildImagesFormArray(
    images: IDeliverableImage[],
    creatorId?: string,
    isViewMode?: boolean,
  ): FormArray<FormGroup<IDeliverableImageForm>> {
    return new FormArray<FormGroup<IDeliverableImageForm>>(
      images.map((image) => this.createImageForm(image, creatorId, isViewMode)),
    );
  }

  private createImageForm(
    image: IDeliverableImage,
    creatorId?: string,
    isViewMode?: boolean,
  ): FormGroup<IDeliverableImageForm> {
    const mockFile = this.createMockFileIfNeeded(image);

    return new FormGroup<IDeliverableImageForm>({
      customCtaValues: new FormControl<ICustomCtaValue[]>(image.customCtaValues ?? [], {
        nonNullable: true,
      }),
      exclusiveTo: new FormControl<string | null>(image.exclusiveTo || creatorId || null),
      globalCtaValues: new FormControl<IGlobalCtaValue[] | null>(image.globalCtaValues ?? null),
      id: new FormControl<string | null>(image.id ?? uuidv4()),
      imageFile: new FormControl<ICustomFile | null>(
        mockFile ?? (image.imageFile as ICustomFile | null) ?? null,
        isViewMode ? [] : [Validators.required, imageExtensionValidator()],
      ),
      imageId: new FormControl<string | null>(image.imageId),
    });
  }

  private createMockFileIfNeeded(image: IDeliverableImage): ICustomFile | null {
    if (!image.imageId || image.imageFile) {
      return null;
    }

    return FileUploaderService.createCustomFile(image.imageId);
  }
}
